import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Collapse,
  IconButton,
  Chip,
  Grid,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import WorkIcon from '@mui/icons-material/Work';

const HeroSection = styled(Box)({
  minHeight: '40vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#fff',
  backgroundColor: '#000',
});

const JobCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  borderRadius: 0,
  border: '1px solid rgba(0,0,0,0.1)',
  boxShadow: 'none',
  '&:hover': {
    borderColor: 'rgba(0,0,0,0.3)',
  },
}));

const ExpandButton = styled(IconButton)(({ expanded }) => ({
  transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
  transition: 'transform 0.3s',
}));

const jobs = [
  {
    id: 1,
    title: 'Electrical Engineer - BESS',
    title_zh: '电气工程师 - 储能系统',
    location: 'Dallas, TX',
    type: 'Full-time',
    type_zh: '全职',
    salary: '$120,000 - $140,000',
    description: 'Design and develop Battery Energy Storage Systems (BESS) for utility-scale projects.',
    description_zh: '为公用事业规模项目设计和开发电池储能系统（BESS）。',
    responsibilities: [
      'Design electrical systems for BESS projects',
      'Perform power system studies and analysis',
      'Coordinate with project teams and clients',
      'Review and approve engineering drawings',
    ],
    responsibilities_zh: [
      '设计储能项目的电气系统',
      '进行电力系统研究和分析',
      '与项目团队和客户协调',
      '审核和批准工程图纸',
    ],
    qualifications: [
      'Bachelor\'s degree in Electrical Engineering',
      '3+ years of experience in power systems',
      'Experience with BESS or renewable energy projects',
      'PE license preferred',
    ],
    qualifications_zh: [
      '电气工程学士学位',
      '3年以上电力系统经验',
      '储能或可再生能源项目经验',
      '优先考虑持有PE执照者',
    ],
    benefits: 'H1B sponsorship available',
    benefits_zh: '可提供H1B签证担保',
  },
  {
    id: 2,
    title: 'Entry-level Project Coordinator',
    title_zh: '初级项目协调员',
    location: 'Dallas, TX',
    type: 'Full-time',
    type_zh: '全职',
    salary: '$45,000 - $65,000',
    description: 'Support project management activities for electrical infrastructure projects.',
    description_zh: '支持电气基础设施项目的项目管理活动。',
    responsibilities: [
      'Assist in project planning and scheduling',
      'Coordinate with vendors and contractors',
      'Maintain project documentation',
      'Track project milestones and deliverables',
    ],
    responsibilities_zh: [
      '协助项目规划和调度',
      '与供应商和承包商协调',
      '维护项目文档',
      '跟踪项目里程碑和交付物',
    ],
    qualifications: [
      'Bachelor\'s degree in Engineering or related field',
      'Strong organizational and communication skills',
      'Proficiency in Microsoft Office',
      'Ability to work in a fast-paced environment',
    ],
    qualifications_zh: [
      '工程或相关领域学士学位',
      '良好的组织和沟通能力',
      '熟练使用Microsoft Office',
      '能够在快节奏环境中工作',
    ],
  },
  {
    id: 3,
    title: 'High-Voltage Equipment Marketing Specialist',
    title_zh: '高压设备市场专员',
    location: 'Dallas, TX',
    type: 'Full-time',
    type_zh: '全职',
    salary: '$65,000 - $145,000',
    description: 'Drive sales and marketing efforts for high-voltage electrical equipment.',
    description_zh: '推动高压电气设备的销售和营销工作。',
    responsibilities: [
      'Develop and execute marketing strategies',
      'Build relationships with utility companies',
      'Prepare technical proposals and presentations',
      'Attend industry conferences and trade shows',
    ],
    responsibilities_zh: [
      '制定和执行营销策略',
      '与电力公司建立关系',
      '准备技术提案和演示',
      '参加行业会议和展会',
    ],
    qualifications: [
      'Bachelor\'s degree in Engineering or Business',
      '5+ years of experience in power equipment sales',
      'Strong technical knowledge of transformers and switchgear',
      'Excellent presentation and negotiation skills',
    ],
    qualifications_zh: [
      '工程或商业学士学位',
      '5年以上电力设备销售经验',
      '对变压器和开关设备有深入的技术知识',
      '出色的演示和谈判技巧',
    ],
  },
  {
    id: 4,
    title: 'Marketing Specialist - Power Transformers',
    title_zh: '市场专员 - 电力变压器',
    location: 'Dallas, TX',
    type: 'Full-time',
    type_zh: '全职',
    salary: 'Competitive',
    description: 'Focus on marketing and sales of power transformers to utility and industrial clients.',
    description_zh: '专注于向电力和工业客户营销和销售电力变压器。',
    responsibilities: [
      'Manage transformer product marketing',
      'Develop customer relationships',
      'Analyze market trends and competition',
      'Support bid preparation and pricing',
    ],
    responsibilities_zh: [
      '管理变压器产品营销',
      '发展客户关系',
      '分析市场趋势和竞争',
      '支持投标准备和定价',
    ],
    qualifications: [
      'Technical background in electrical engineering',
      'Experience in transformer or power equipment industry',
      'Strong analytical and communication skills',
      'Willingness to travel',
    ],
    qualifications_zh: [
      '电气工程技术背景',
      '变压器或电力设备行业经验',
      '较强的分析和沟通能力',
      '愿意出差',
    ],
  },
  {
    id: 5,
    title: 'Project Coordinator Intern',
    title_zh: '项目协调员实习生',
    location: 'Dallas, TX',
    type: 'Internship',
    type_zh: '实习',
    salary: '$15/hour',
    description: 'Gain hands-on experience in project coordination for electrical infrastructure projects.',
    description_zh: '获得电气基础设施项目协调的实践经验。',
    responsibilities: [
      'Support project documentation',
      'Assist with scheduling and coordination',
      'Learn project management tools and processes',
      'Participate in team meetings',
    ],
    responsibilities_zh: [
      '支持项目文档工作',
      '协助调度和协调',
      '学习项目管理工具和流程',
      '参与团队会议',
    ],
    qualifications: [
      'Currently pursuing degree in Engineering or related field',
      'Strong attention to detail',
      'Eager to learn and grow',
      'Good communication skills',
    ],
    qualifications_zh: [
      '正在攻读工程或相关领域学位',
      '注重细节',
      '渴望学习和成长',
      '良好的沟通能力',
    ],
  },
];

function Careers() {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const isZh = lang === 'zh';
  const [expanded, setExpanded] = useState({});

  const toggleExpand = (id) => {
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <Box>
      <HeroSection>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', py: { xs: 8, md: 0 } }}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Typography
                variant="overline"
                sx={{ color: 'rgba(255,255,255,0.6)', letterSpacing: '0.2em', fontSize: '12px', display: 'block', mb: 2 }}
              >
                {isZh ? '招聘' : 'CAREERS'}
              </Typography>
              <Typography variant="h2" sx={{ fontWeight: 500, mb: 3 }}>
                {isZh ? '开放职位' : 'Open Positions'}
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.7)', maxWidth: 600, mx: 'auto' }}>
                {isZh ? '加入我们的团队，共同推动电网前行' : 'Join our team and help drive the grid forward'}
              </Typography>
            </motion.div>
          </Box>
        </Container>
      </HeroSection>

      <Box sx={{ py: 10, backgroundColor: '#f8f8f8' }}>
        <Container maxWidth="md">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            {jobs.map((job) => (
              <JobCard key={job.id}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h5" sx={{ fontWeight: 500, mb: 1 }}>
                        {isZh ? job.title_zh : job.title}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
                          <LocationOnIcon sx={{ fontSize: 18, mr: 0.5 }} />
                          <Typography variant="body2">{job.location}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
                          <WorkIcon sx={{ fontSize: 18, mr: 0.5 }} />
                          <Typography variant="body2">{isZh ? job.type_zh : job.type}</Typography>
                        </Box>
                        <Chip label={job.salary} size="small" sx={{ borderRadius: 0 }} />
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {isZh ? job.description_zh : job.description}
                      </Typography>
                    </Box>
                    <ExpandButton expanded={expanded[job.id]} onClick={() => toggleExpand(job.id)}>
                      <ExpandMoreIcon />
                    </ExpandButton>
                  </Box>

                  <Collapse in={expanded[job.id]}>
                    <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid rgba(0,0,0,0.1)' }}>
                      <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                            {isZh ? '职责' : 'Responsibilities'}
                          </Typography>
                          <ul style={{ margin: 0, paddingLeft: 20 }}>
                            {(isZh ? job.responsibilities_zh : job.responsibilities).map((item, i) => (
                              <li key={i}><Typography variant="body2">{item}</Typography></li>
                            ))}
                          </ul>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                            {isZh ? '要求' : 'Qualifications'}
                          </Typography>
                          <ul style={{ margin: 0, paddingLeft: 20 }}>
                            {(isZh ? job.qualifications_zh : job.qualifications).map((item, i) => (
                              <li key={i}><Typography variant="body2">{item}</Typography></li>
                            ))}
                          </ul>
                        </Grid>
                      </Grid>
                      {job.benefits && (
                        <Box sx={{ mt: 2 }}>
                          <Chip
                            label={isZh ? job.benefits_zh : job.benefits}
                            color="primary"
                            size="small"
                            sx={{ borderRadius: 0 }}
                          />
                        </Box>
                      )}
                    </Box>
                  </Collapse>
                </CardContent>
              </JobCard>
            ))}
          </motion.div>
        </Container>
      </Box>
    </Box>
  );
}

export default Careers;
